<script lang="ts">
    import { theme } from '$theme/theme.svelte';
    import Text from '$components/common/text/text.svelte';
    import Loading from '$assets/icons/loading.svelte';
    import type { ComponentType } from 'svelte';

    export let disabled = false;
    export let size: 'large' | 'medium' | 'small' | 'xSmall' | 'xxSmall' = 'medium';
    export let variant: 'primary' | 'outline' | 'text' = 'primary';

    export let loading = false;
    export let loadingText = '';
    export let icon: ComponentType | undefined = undefined;
    export let iconProps: {
        width?: string;
        height?: string;
        fill?: string;
        color?: string;
        stroke?: string;
        class?: string;
    } = {};
    export let iconEnd = false;

    $: slot = Object.keys($$slots).length;

    export let textProps = {};

    export let fullWidth = false;

    let className: string | undefined = undefined;
    export { className as class };
</script>

<button
    on:click
    {disabled}
    class:large={size === 'large'}
    class:medium={size === 'medium'}
    class:small={size === 'small'}
    class:xSmall={size === 'xSmall'}
    class:xxSmall={size === 'xxSmall'}
    class:primary={variant === 'primary'}
    class:outline={variant === 'outline'}
    class:text={variant === 'text'}
    class:primary-loading={variant === 'primary' && loading}
    class:outline-loading={variant === 'outline' && loading}
    class:text-loading={variant === 'text' && loading}
    class:loading={loading && !loadingText}
    class:fullWidth
    class={className ?? ''}
    style:padding={slot ? '' : '0px'}
    {...$$restProps}
>
    {#if !iconEnd}
        {#if loading}
            <Loading
                fill={variant === 'primary' ? $theme.colors.white : $theme.colors.primary[500]}
                height="19.5"
                width="21.5"
                style="margin-right: {loadingText ? 'var(--spacing-05)' : '0px'}"
            />
        {:else if icon}
            <svelte:component
                this={icon}
                {...iconProps}
                style="margin-right: {slot ? 'var(--spacing-05)' : '0px'}"
            />
        {/if}
    {/if}

    {#if loading}
        {#if loadingText}
            <Text {...textProps}>
                {loadingText}
            </Text>
        {/if}
    {:else if slot}
        <Text {...textProps}>
            <slot />
        </Text>
    {/if}

    {#if iconEnd}
        {#if loading}
            <Loading
                fill={variant === 'primary' ? $theme.colors.white : $theme.colors.primary[500]}
                height={19.5}
                width={21.5}
                style="margin-left: {loadingText ? 'var(--spacing-05)' : '0px'}"
            />
        {:else if icon}
            <svelte:component
                this={icon}
                {...iconProps}
                style="margin-left: {slot ? 'var(--spacing-05)' : '0px'}"
            />
        {/if}
    {/if}
</button>

<style>
    button {
        border-radius: 100px;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        white-space: nowrap;
    }

    .large {
        min-height: 56px;
        min-width: 56px;
        padding-left: 24px;
        padding-right: 24px;
        padding-top: 8px;
        padding-bottom: 8px;
    }

    .medium {
        min-height: 48px;
        min-width: 48px;
        padding-left: 24px;
        padding-right: 24px;
        padding-top: 8px;
        padding-bottom: 8px;
    }

    .small {
        min-height: 40px;
        min-width: 40px;
        padding-left: 16px;
        padding-right: 16px;
        padding-top: 8px;
        padding-bottom: 8px;
    }

    .xSmall {
        min-height: 32px;
        min-width: 32px;
        padding-left: 16px;
        padding-right: 16px;
        padding-top: 6px;
        padding-bottom: 6px;
    }

    .xxSmall {
        height: var(--spacing-11);
        width: var(--spacing-11);
    }

    .primary {
        background-color: var(--colors-secondary-900);
        color: var(--colors-white);
        border: 2px solid var(--colors-secondary-900);
    }

    .primary:hover {
        background-color: var(--colors-primary-700);
        border-color: var(--colors-primary-700);
    }

    .primary:focus {
        border-color: var(--colors-primary-800);
        background-color: var(--colors-primary-500);
    }

    .primary:disabled {
        background-color: var(--colors-neutral-50);
        color: var(--colors-neutral-400);
        border: var(--colors-neutral-50);
        cursor: not-allowed;
    }

    .primary-loading {
        background-color: var(--colors-primary-700);
        border-color: var(--colors-primary-700);
    }

    .outline {
        background-color: transparent;
        color: var(--colors-primary-900);
        border: 1px solid var(--colors-primary-400);
    }

    .outline:hover:enabled {
        background-color: #cbd7e8;
        border-color: var(--colors-primary-500);
    }

    .outline:focus {
        border-color: var(--colors-primary-500);
        outline: none;
    }

    .outline:disabled {
        color: var(--colors-neutral-400);
        border: 1px solid var(--colors-neutral-100);
        cursor: not-allowed;
    }

    .outline-loading {
        background-color: rgba(50, 125, 227, 0.2);
        border-color: var(--colors-primary-400);
    }

    .text {
        border: none;
        color: var(--colors-primary-900);
        background-color: transparent;
    }

    .text:hover:enabled {
        background-color: rgba(50, 125, 227, 0.2);
    }

    .text:focus {
        background-color: var(--colors-primary-200);
    }

    .text:disabled {
        color: var(--colors-neutral-400);
        cursor: not-allowed;
    }

    .text-loading {
        background-color: rgba(50, 125, 227, 0.2);
    }

    .loading {
        padding: 0px;
    }

    .fullWidth {
        width: 100%;
    }
</style>
